import type { ReactNode } from "react";
import React, { createContext, useContext, useState, useCallback } from "react";

import axios, { type AxiosResponse } from "axios";

import type {
  HealthPlanList,
  ProviderDirectoryHealthPlansList200Response,
} from "~/src/types";

export interface HealthPlanDataProps {
  results: HealthPlanList[];
  loading?: boolean;
  error?: string | null;
  count?: number;
}

interface HealthPlanContextType {
  healthPlanData: HealthPlanDataProps;
  fetchHealthPlans: (searchParams?: string) => void;
  healthPlanResults: HealthPlanList[];
  setHealthPlanData: (data: HealthPlanDataProps) => void;
  setHealthPlanResults: (results: HealthPlanList[]) => void;
}

const HealthPlanContext = createContext<HealthPlanContextType | undefined>(
  undefined,
);

export const HealthPlanProvider = ({ children }: { children: ReactNode }) => {
  const [healthPlanData, setHealthPlanData] = useState<HealthPlanDataProps>({
    results: [],
    loading: true,
    error: null,
  });

  const fetchHealthPlans = useCallback((searchParams: string = "") => {
    setHealthPlanData((prev) => {
      return {
        ...prev,
        loading: true,
      };
    });

    axios
      .get(
        `provider_directory/health_plans${searchParams.startsWith("?") ? "" : "?"}${searchParams}`,
      )
      .then(
        (res: AxiosResponse<ProviderDirectoryHealthPlansList200Response>) => {
          setHealthPlanData({
            results: res.data.result?.results || [],
            count: res.data.result?.count || 0,
            loading: false,
            error: null,
          });
        },
      )
      .catch((err) => {
        console.error(err);
        setHealthPlanData({
          results: [],
          loading: false,
          error: "Something went wrong loading health plans",
        });
      });
  }, []);

  const setHealthPlanDataFunc = useCallback((data: HealthPlanDataProps) => {
    setHealthPlanData((prev) => {
      return {
        ...prev,
        ...data,
      };
    });
  }, []);

  const setHealthPlanResults = useCallback((results: HealthPlanList[]) => {
    setHealthPlanData((prev) => {
      return {
        ...prev,
        results,
        loading: false,
      };
    });
  }, []);

  const contextValue: HealthPlanContextType = {
    healthPlanData,
    fetchHealthPlans,
    healthPlanResults: healthPlanData.results,
    setHealthPlanData: setHealthPlanDataFunc,
    setHealthPlanResults,
  };

  return (
    <HealthPlanContext.Provider value={contextValue}>
      {children}
    </HealthPlanContext.Provider>
  );
};

export const useHealthPlans = () => {
  const context = useContext(HealthPlanContext);

  if (context === undefined) {
    throw new Error("useHealthPlans must be used within a HealthPlanContext");
  }

  return context;
};
